import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import copy from 'copy-to-clipboard';
import SidepanelDefaultRow from '../side-panel/side-panel-default-row';
import IconCopy from '../icons/copy-two';
import IconUp from '../icons/up';
import IconDown from '../icons/down';
import IconClose from '../icons/close';
import IconLoader from '../icons/loader';
import { TransactionExternalAccountRowTypes } from './types';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import IconLink from '../icons/link-two';
import usePrevious from '../../hooks/use-previous';

import styles from './transaction.module.css';

let searchTimeout: NodeJS.Timeout;

export default function ExternalAccountRow({
  title,
  name,
  value,
  control,
  enableEdit,
  placeholder,
  externalAccounts,
  disabled,
  isLoading,
  setValue,
  tabIndex,
  copyValue,
  copyEnabled,
  error,
  selectedExternalAccount,
  setSelelectedExternalAccount,
  pspIntegrationAccountId,
  onSearch,
  onClear,
  onGetList,
  pagination,
  externalAccountsNextPageIsLoading,
  transactionId,
  overdraftEnabled,
  isOutgoing,
  clearErrors,
}: TransactionExternalAccountRowTypes) {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [toggledOn, toggle] = useState(false);
  const [selectedNode, setSelectedNode] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const prevOpen = usePrevious(toggledOn);

  const onCopy = () => {
    setCopyIsSuccess(true);
    copy(copyValue as string);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const onScroll = (e: any) => {
    const nodes = ulRef?.current?.querySelectorAll('li');

    if (nodes) {
      const firstNode = nodes[0];
      const elHeight = firstNode.clientHeight;

      const node = nodes[Math.round(e.target.scrollTop / elHeight)];

      if (node) {
        // @ts-ignore
        const currentIndex = [].indexOf.call(nodes, node);

        if (currentIndex && currentIndex !== undefined && currentIndex !== -1) {
          const prevNode = nodes[selectedNode];
          const currentNode = nodes[currentIndex];

          prevNode?.classList?.remove(styles.hovered);
          currentNode?.classList.add(styles.hovered);

          setSelectedNode(currentIndex);
        }
      }
    }

    if (ulRef && ulRef.current) {
      const ul = ulRef.current;

      const scroll = e.target.scrollTop;

      if (
        scroll > ul.scrollHeight - ul.clientHeight - 50 &&
        pagination?.hasNextPage &&
        onGetList
      ) {
        onGetList({ search: searchQuery || '', cursor: pagination.endCursor });
      }
    }
  };

  const onOpen = () => {
    toggle(true);

    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const onSelectNode = ({
    node,
    selected,
  }: {
    node: HTMLLIElement;
    selected: number;
  }) => {
    if (node) {
      node.classList.add(styles.hovered);

      const elHeight = node.clientHeight;
      const scrollTop = ulRef?.current?.scrollTop;

      if (scrollTop !== undefined && ulRef?.current) {
        const ulHeight = ulRef?.current?.clientHeight;
        const viewport = scrollTop + ulHeight;

        const elOffset = elHeight * selected;

        if (elOffset < scrollTop || elOffset + elHeight > viewport) {
          if (selectedNode !== 1) {
            ulRef?.current.scrollTo(0, elOffset);
          } else {
            ulRef?.current.scrollTo(0, 0);
          }
        }
      }
    }
  };

  const onEnter = () => {
    externalAccounts?.forEach((item, index) => {
      if (index === selectedNode) {
        if (setValue) {
          if (isOutgoing) {
            setValue('senderAccountNumber', item?.account?.fullNumber);
          } else {
            setValue('recipientAccountNumber', item?.account?.fullNumber);
          }

          setValue('pspIntegrationAccountId', item.id);
          setValue('currencyId', '');
          setValue('pspPartnerId', '');
          setValue('pspPartnerAccountNumber', '');
          setValue('paymentType', '');
        }

        setSelelectedExternalAccount(item);

        toggle(false);
      }
    });
  };

  const onUlKeydown = (e: { code: string; preventDefault: () => void }) => {
    const nodes = ulRef?.current?.querySelectorAll('li');
    let nextNode;

    if (nodes) {
      if (e.code === 'ArrowDown' && nodes[selectedNode + 1]) {
        setSelectedNode(selectedNode + 1);
        nextNode = selectedNode + 1;

        onSelectNode({
          node: nodes[selectedNode + 1],
          selected: selectedNode + 1,
        });
      }

      if (e.code === 'ArrowUp' && selectedNode !== 0) {
        nextNode = selectedNode - 1;

        setSelectedNode(selectedNode - 1);
        onSelectNode({
          node: nodes[selectedNode - 1],
          selected: selectedNode - 1,
        });
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        const prevLi = nodes[selectedNode];

        if (prevLi && nodes[nextNode as number]) {
          prevLi.classList.remove(styles.hovered);
        }
      }

      e.preventDefault();
    }
  };

  useEffect(() => {
    if (ulRef && ulRef.current && toggledOn) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes[0]) {
        const firstNode = nodes[0];

        if (firstNode) {
          firstNode.classList.add(styles.hovered);
        }
      }
    }
  }, [ulRef, toggledOn]);

  useEffect(() => {
    if (!toggledOn && prevOpen) {
      setSelectedNode(0);

      ulRef?.current?.removeEventListener('keydown', () => {});
    }
  }, [ulRef, toggledOn, prevOpen]);

  useEffect(() => {
    if (
      transactionId &&
      pspIntegrationAccountId &&
      !searchQuery &&
      externalAccounts
    ) {
      const currentAccount = externalAccounts?.find(
        (item) => item.id === pspIntegrationAccountId,
      );

      if (currentAccount) {
        setSearchQuery(currentAccount?.account?.description);
      }
    }
  }, [transactionId, pspIntegrationAccountId, searchQuery, externalAccounts]);

  useEffect(() => {
    if (
      toggledOn &&
      pspIntegrationAccountId &&
      externalAccounts &&
      ulRef &&
      ulRef.current
    ) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes) {
        const currentItem = externalAccounts?.find(
          (item) => item.id === pspIntegrationAccountId,
        );

        if (currentItem) {
          const currentIndex = externalAccounts?.indexOf(currentItem);

          const currentNode = nodes[currentIndex];

          if (currentNode) {
            const positionTop = currentNode.offsetTop;

            if (positionTop) {
              ulRef.current.scrollTop = positionTop;

              setSelectedNode(currentIndex);
            }
          }
        }
      }
    }
  }, [pspIntegrationAccountId, externalAccounts, ulRef, toggledOn]);

  let content;

  if (enableEdit) {
    content = (
      <ClickOutsideDetector setOptionsListVisible={toggle}>
        <div
          className={styles.externalAccountsWrapper}
          onKeyDown={(e) => {
            if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
              onUlKeydown(e);
            }

            if (e.code === 'Enter') {
              onEnter();
            }
          }}
          role="button"
          tabIndex={-1}
        >
          <div className={styles.externalAccountsTitle}>{title}</div>
          <Controller
            name={name}
            control={control}
            defaultValue={value}
            render={({ field: { value: fieldValue, onChange } }) => (
              <>
                <div className={styles.externalAccountsValueWrapper}>
                  {copyEnabled && (
                    <>
                      {enableEdit && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.externalCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={onCopy}
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                      {!enableEdit && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={onCopy}
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </>
                  )}
                  {toggledOn ? (
                    <IconUp className={styles.arrow} />
                  ) : (
                    <IconDown className={styles.arrow} />
                  )}
                  <input
                    type="text"
                    className={`${styles.externalAccountsSearchInput} ${
                      disabled || (isLoading && !externalAccounts)
                        ? styles.disabled
                        : ''
                    } ${error ? styles.error : ''}`}
                    onFocus={onOpen}
                    onChange={(e) => {
                      clearTimeout(searchTimeout);
                      setSearchQuery(e.target.value);
                      setSelectedNode(0);

                      if (error && clearErrors) {
                        clearErrors([name]);
                      }

                      if (onSearch) {
                        searchTimeout = setTimeout(() => {
                          onSearch(e.target.value);
                        }, 500);
                      }
                    }}
                    value={
                      selectedExternalAccount &&
                      searchQuery?.length > 22 &&
                      !toggledOn
                        ? `${searchQuery.slice(0, -10)}...`
                        : searchQuery
                    }
                    onKeyDown={(e) => {
                      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                        onUlKeydown(e);
                      }

                      if (e.code === 'Enter') {
                        onEnter();
                      }

                      if (e.code === 'Tab') {
                        toggle(false);
                      }
                    }}
                    tabIndex={tabIndex}
                    placeholder={placeholder}
                  />
                  <div className={styles.externalAccountValue}>
                    {fieldValue && !toggledOn && (
                      <div className={styles.externalAccountIban}>
                        <div>{selectedExternalAccount?.iban}</div>
                      </div>
                    )}
                    {(fieldValue || searchQuery) && !disabled && (
                      <button
                        type="button"
                        className={`${styles.iconWrapper} ${styles.iconWrapperClear}`}
                        onClick={() => {
                          setSearchQuery('');
                          onChange('');

                          if (onClear) {
                            onClear();
                          }
                        }}
                        tabIndex={-1}
                        aria-label="Close"
                      >
                        <IconClose className={styles.icon} />
                      </button>
                    )}
                  </div>
                </div>
                {error && <div className={styles.externalError}>{error}</div>}
                {toggledOn && (
                  <ul
                    className={`${styles.externalAccountsList} ${
                      isLoading && !externalAccountsNextPageIsLoading
                        ? styles.externalAccountsListLoading
                        : ''
                    }`}
                    ref={ulRef}
                    onScroll={onScroll}
                  >
                    {!externalAccounts?.length && !isLoading && (
                      <li className={styles.notFound}>Not Found</li>
                    )}
                    {isLoading && !externalAccountsNextPageIsLoading && (
                      <IconLoader />
                    )}
                    {externalAccounts &&
                      externalAccounts?.length > 0 &&
                      (!isLoading ||
                        (isLoading && externalAccountsNextPageIsLoading)) &&
                      externalAccounts.map((item) => (
                        <li
                          key={item.id}
                          className={`select-item ${
                            pspIntegrationAccountId === item.id
                              ? styles.selected
                              : ''
                          }`}
                        >
                          <button
                            type="button"
                            tabIndex={-1}
                            className={styles.selectExternalAccount}
                            onClick={() => {
                              const currentExternalAccount =
                                externalAccounts?.find(
                                  (externalAccount) =>
                                    externalAccount.id === item.id,
                                );

                              if (currentExternalAccount) {
                                onChange(currentExternalAccount.id);

                                if (setValue) {
                                  if (isOutgoing) {
                                    setValue(
                                      'senderAccountNumber',
                                      currentExternalAccount?.account
                                        ?.fullNumber,
                                    );
                                  } else {
                                    setValue(
                                      'recipientAccountNumber',
                                      currentExternalAccount?.account
                                        ?.fullNumber,
                                    );
                                  }

                                  setValue('currencyId', '');
                                  setValue('pspPartnerId', '');
                                  setValue('pspPartnerAccountNumber', '');
                                  setValue('paymentType', '');
                                }

                                setSearchQuery(
                                  currentExternalAccount?.account?.description?.replace(
                                    '\t',
                                    '',
                                  ),
                                );

                                setSelelectedExternalAccount(
                                  currentExternalAccount,
                                );

                                toggle(false);
                              }
                            }}
                          >
                            <div className={styles.selectExternalAccountTop}>
                              <div
                                className={styles.selectExternalAccountTopName}
                              >
                                {item?.account?.description}
                              </div>
                              <div
                                className={styles.selectExternalAccountTopIban}
                              >
                                {item?.iban}
                              </div>
                            </div>
                            <div className={styles.externalAccountPaymentTypes}>
                              {item?.pspIntegrationPayment?.paymentsTypes?.map(
                                (paymentType) => {
                                  const typeTitle = paymentType
                                    .split('_')
                                    .map(
                                      (type) =>
                                        `${type.charAt(0)}${type
                                          .slice(1)
                                          .toLowerCase()}`,
                                    )
                                    ?.join(' ');

                                  return (
                                    <div
                                      key={paymentType}
                                      className={styles.paymentType}
                                    >
                                      {typeTitle}
                                    </div>
                                  );
                                },
                              )}
                              {!item?.pspIntegrationPayment?.paymentsTypes
                                .length && <div>-</div>}
                            </div>
                            <div className={styles.selectExternalAccountBottom}>
                              <NavLink
                                to={`/accounts/external/accounts-list/${item.id}`}
                                target="_blank"
                                className={styles.externalAccountNumber}
                              >
                                <IconLink />
                                {item?.account?.fullNumber}
                                {overdraftEnabled ? (
                                  <span>
                                    {item?.account?.overdraftEnabled
                                      ? 'Overdraft'
                                      : ''}
                                  </span>
                                ) : (
                                  ''
                                )}
                              </NavLink>
                              <div className={styles.externalAccountBank}>
                                {item?.pspPartner?.companyName}
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    {externalAccountsNextPageIsLoading && (
                      <li className={styles.nextPageLoader}>
                        <IconLoader />
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
          />
          {selectedExternalAccount && (
            <div className={styles.selectedExternalAccountInfo}>
              <div className={styles.selectedExternalAccountPaymentTypes}>
                {selectedExternalAccount?.pspIntegrationPayment?.paymentsTypes?.map(
                  (paymentType) => {
                    const typeTitle = paymentType
                      .split('_')
                      .map(
                        (type) =>
                          `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                      )
                      ?.join(' ');

                    return (
                      <div key={paymentType} className={styles.paymentType}>
                        {typeTitle}
                      </div>
                    );
                  },
                )}
              </div>
              <NavLink
                to={`/accounts/external/accounts-list/${selectedExternalAccount.id}`}
                target="_blank"
                className={styles.externalAccountNumber}
              >
                <IconLink />
                {selectedExternalAccount?.account?.fullNumber}
              </NavLink>
            </div>
          )}
        </div>
      </ClickOutsideDetector>
    );
  } else {
    content = value;
  }

  return (
    <SidepanelDefaultRow
      title={enableEdit ? undefined : title}
      valueClassName={`${copyEnabled && enableEdit ? styles.rowWithCopy : ''} ${
        copyEnabled && !enableEdit ? styles.readCopyButtonWrapper : ''
      }`}
    >
      {content}
    </SidepanelDefaultRow>
  );
}
